import BN from "bn.js";
import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useMemo } from "react";
import { combineLatest, merge, of, Subject } from "rxjs";
import { None, Option, Some } from "space-monad";
import { useFetchable } from "../../../store/common/fetchable";
import { isFetchableSuccess } from "../../../store/common/fetchable/helpers";
import { StakingDTO } from "../../../store/staking/models/dto";
import { Staking } from "../../../store/staking/reducer";
import {
  configurations,
  refreshWalletBalance,
  useBrowserWallet,
  web3$,
} from "../../../store/wallets/crypto";
import {
  chainsMap,
  chainSpecs,
} from "../../../store/wallets/crypto/chain-config";

interface ContractWalletInfo {
  isVerified: boolean;
  minStake: BN;
  maxStake: BN;
  totalStakedTokens: BN;
  balance: BN;
  poolSize: BN;
  rewards: {
    full: BN;
    effective: BN;
    rate: number;
    daysSinceStake: number;
  };
  lastStaked: Moment;
  earlyStakeLimit: Moment;
}

function getRewardRate(
  daysSinceStake: number,
  stats: StakingDTO.Responses.GetStats.T[number]
): number {
  if (daysSinceStake > 365) {
    return 1;
  }
  if (daysSinceStake > 180) {
    return stats.secondUnStakePenalty / 100;
  }
  if (daysSinceStake > 0) {
    return stats.firstUnStakePenalty / 100;
  }
  return 0;
}

export function useStaking() {
  const { accounts } = useBrowserWallet();
  const stats = useFetchable(Staking.stats);

  // -------- STAKING ---------

  const [
    selectedStakingContractId,
    setSelectedStakingContractId,
  ] = React.useState<Option<string>>(None);

  // state for verification contract (the one staking uses to verify users)
  const [verifcationContract, setVerifcationContract] = React.useState<
    Option<any>
  >(None);

  const [allStakingContracts, setAllStakingContracts] = React.useState<
    Option<
      Array<
        StakingDTO.Responses.GetStats.T[number] & {
          walletInfo?: ContractWalletInfo;
          instance?: any; // web3 instance
        }
      >
    >
  >(None);

  // Subject used to refrest stats in case the user stakes
  const refreshStats = useMemo(() => new Subject<void>(), []);

  // Effect to set all staking contracts
  useEffect(() => {
    const sub = combineLatest([merge(of(1), refreshStats), web3$]).subscribe(
      async ([_, web3Opt]) => {
        if (isFetchableSuccess(stats)) {
          const allContracts = await Option.all([accounts, web3Opt]).fold(
            async () => Some(stats.data),
            async ([{ coinbase, networkId }, web3]) => {
              const conf = configurations[chainsMap[networkId]];

              if (!conf || !conf.contracts || !conf.contracts.skrtStaking) {
                return Some(stats.data);
              }

              const { skrtStaking } = conf.contracts;

              const verificationContract: any = new web3.eth.Contract(
                skrtStaking.verification.abi,
                skrtStaking.verification.address
              );

              return Some(
                await Promise.all(
                  stats.data.map(async (d) => {
                    if (d.blockchain !== chainsMap[networkId]) {
                      return d;
                    }
                    const abi = skrtStaking.origins[d.address];
                    if (!abi) {
                      return d;
                    }
                    const contract = new web3.eth.Contract(abi, d.address);

                    const [
                      bal,
                      {
                        minimumStakingAmount,
                        maximumStakingAmount,
                        stakingPoolSize,
                      },
                      rewards,
                      lastStaked,
                      unstakeLim,
                      isVer,
                      totalStaked,
                    ] = await Promise.all([
                      contract.methods.stakeBalance(coinbase).call(),
                      contract.methods.stakingParameters().call(),
                      contract.methods.getOnlyRewards(coinbase).call(),
                      contract.methods.lastStakeClaimed(coinbase).call(),
                      contract.methods.unStakeStartSeconds().call(),
                      verificationContract.methods
                        .checkAddress(coinbase)
                        .call(),
                      contract.methods.totalStakedTokens().call(),
                    ]);

                    const stakedBalance = new BN(bal);
                    const lastStakedComputed = moment(moment.unix(lastStaked));
                    const daysSinceStake = moment().diff(
                      lastStakedComputed,
                      "days"
                    );
                    const rewardRate =
                      stats.status === "ready"
                        ? getRewardRate(daysSinceStake, d)
                        : 0;

                    return {
                      ...d,
                      instance: contract,
                      walletInfo: {
                        totalStakedTokens: new BN(totalStaked),
                        balance: stakedBalance,
                        maxStake: new BN(maximumStakingAmount),
                        minStake: new BN(minimumStakingAmount),
                        poolSize: new BN(stakingPoolSize),
                        isVerified: isVer,
                        rewards: {
                          full: new BN(rewards),
                          effective: new BN(rewards).muln(rewardRate),
                          rate: rewardRate,
                          daysSinceStake,
                        },
                        lastStaked: lastStakedComputed,
                        earlyStakeLimit: moment.unix(
                          parseInt(lastStaked, 10) + parseInt(unstakeLim, 10)
                        ),
                      },
                    };
                  })
                )
              );
            }
          );
          setVerifcationContract(Some(verifcationContract));
          setAllStakingContracts(allContracts);
        }
      }
    );
    return () => sub.unsubscribe();
  }, [accounts, selectedStakingContractId, stats]);

  // state for loaded web3 instance of the current staking contract
  const stakingContract = useMemo(
    () =>
      Option.all([selectedStakingContractId, allStakingContracts]).map(
        ([id, contracts]) => {
          return contracts.find((c) => c.address === id);
        }
      ),
    [selectedStakingContractId, allStakingContracts]
  );

  // state for checking if the staking contract is available
  const isChainSupported = useMemo(() => stakingContract.isDefined(), [
    stakingContract,
  ]);

  // -------- SKRT Contract ---------

  // state for loaded SKRT contract
  const [skrtContract, setSkrtContract] = React.useState<Option<any>>(None);

  // state for decimal
  const [decimals, setDecimal] = React.useState<Option<number>>(None);

  useEffect(() => {
    const sub = web3$.subscribe((web3Opt) => {
      Option.all([accounts, web3Opt]).fold(
        () => setSkrtContract(None),
        ([{ networkId }, web3]) => {
          const netConfig = configurations[chainsMap[networkId]];
          if (!netConfig) {
            setSkrtContract(None);
            setDecimal(None);
          } else {
            const { skrt } = netConfig.contracts;
            if (!skrt) {
              setSkrtContract(None);
              setDecimal(None);
            } else {
              setDecimal(Some(skrt.decimals));
              setSkrtContract(
                Some(new web3.eth.Contract(skrt.abi, skrt.address))
              );
            }
          }
        }
      );
    });

    return () => sub.unsubscribe();
  }, [accounts, web3$, setSkrtContract, configurations, chainsMap]);

  return {
    stake: useCallback(
      async (amount: BN) =>
        Option.all([
          accounts,
          selectedStakingContractId,
          skrtContract,
          stakingContract.map((x) => x.instance),
        ]).fold(
          () => Promise.reject(new Error("Provider is not available")),
          async ([
            { coinbase, networkId },
            selectedStakingContractId,
            skrtContractActual,
            stakingContractActual,
          ]) => {
            const currentAllowance = new BN(
              await skrtContractActual.methods
                .allowance(coinbase, selectedStakingContractId)
                .call()
            );

            const gasPriceInWei = await chainSpecs[networkId].gasPrice();

            if (currentAllowance.lt(amount)) {
              await skrtContractActual.methods
                .approve(selectedStakingContractId, amount.toString())
                .send({ from: coinbase, gasPrice: gasPriceInWei.get() });
            }

            await stakingContractActual.methods
              .stake(amount.toString())
              .send({ from: coinbase, gasPrice: gasPriceInWei.get() });

            refreshStats.next();
            refreshWalletBalance.next();
          }
        ),
      [
        accounts,
        stakingContract,
        skrtContract,
        refreshStats,
        refreshWalletBalance,
        chainSpecs,
      ]
    ),
    unstake: useCallback(
      async () =>
        Option.all([accounts, stakingContract.map((x) => x.instance)]).fold(
          () => Promise.reject(new Error("Provider is not available")),
          async ([{ coinbase }, stakingContractActual]) => {
            await stakingContractActual.methods.unStake().send({
              from: coinbase,
            });

            refreshStats.next();
            refreshWalletBalance.next();
          }
        ),
      [accounts, stakingContract, refreshStats, refreshWalletBalance]
    ),
    claimRewards: useCallback(
      async (stake: boolean) =>
        Option.all([accounts, stakingContract.map((x) => x.instance)]).fold(
          () => Promise.reject(new Error("Provider is not available")),
          async ([{ coinbase, networkId }, stakingContractActual]) => {
            const gasPriceInWei = await chainSpecs[networkId].gasPrice();

            stake
              ? await stakingContractActual.methods
                  .claimRewardsAndStake()
                  .send({ from: coinbase, gasPrice: gasPriceInWei.get() })
              : await stakingContractActual.methods
                  .claimRewards()
                  .send({ from: coinbase, gasPrice: gasPriceInWei.get() });

            refreshStats.next();
            refreshWalletBalance.next();
          }
        ),
      [accounts, stakingContract, refreshStats, refreshWalletBalance]
    ),
    decimals,
    isChainSupported,
    stats,
    currentContract: stakingContract,
    setStakingContractId: setSelectedStakingContractId,
    stakingContractId: selectedStakingContractId,
    allContracts: allStakingContracts,
  };
}
